import React, { useRef, useState } from 'react';
import { FiPlus, FiSend, FiAlertTriangle, FiUsers, FiRefreshCw, FiLink } from 'react-icons/fi';
import { useNotifications } from '../hooks/useNotifications';
import { useSettings } from '../hooks/useSettings';
import '../styles/notifications.scss';

const STATUS_OPTIONS = [
    { value: '', label: 'الكل' },
    { value: 'pending', label: 'معلق' },
    { value: 'accepted', label: 'مقبول' },
    { value: 'rejected', label: 'مرفوض' }
];

const STATUS_LABELS = { pending: 'معلق', accepted: 'مقبول', rejected: 'مرفوض' };

const EMPTY_FILTERS = { city: [], referenceNumber: 'APP-', phoneNumber: '', status: '', minAge: '', maxAge: '' };

// "APP-" is just the default pre-filled prefix, not a real filter the admin chose
const hasReferenceNumberFilter = (filters) => !!filters.referenceNumber && filters.referenceNumber !== 'APP-';

const hasAnyFilter = (filters) => (
    (filters.city && filters.city.length > 0) ||
    hasReferenceNumberFilter(filters) || filters.phoneNumber || filters.status ||
    filters.minAge || filters.maxAge
);

export const Notifications = () => {
    const {
        phoneNumbers,
        isLoadingRecipients,
        isSending,
        hasSearched,
        fetchRecipients,
        resetRecipients,
        sendNotification,
        history,
        isLoadingHistory,
        pendingRejectionCount,
        isSendingRejectionBatch,
        sendRejectionBatch,
        resendingHistoryId,
        resendFailed
    } = useNotifications();
    const { content: settingsContent } = useSettings();
    const cities = settingsContent.cities || [];

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [filterDraft, setFilterDraft] = useState(EMPTY_FILTERS);
    const [message, setMessage] = useState('');
    const [showLinkInput, setShowLinkInput] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    const messageRef = useRef(null);

    const openModal = () => {
        setFilterDraft(EMPTY_FILTERS);
        resetRecipients();
        setMessage('');
        setShowLinkInput(false);
        setLinkUrl('');
        setIsModalOpen(true);
    };

    const closeModal = () => setIsModalOpen(false);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilterDraft((prev) => ({ ...prev, [name]: value }));
    };

    const toggleCity = (city) => {
        setFilterDraft((prev) => ({
            ...prev,
            city: prev.city.includes(city) ? prev.city.filter((c) => c !== city) : [...prev.city, city]
        }));
    };

    const toggleAllCities = () => {
        setFilterDraft((prev) => ({
            ...prev,
            city: prev.city.length === cities.length ? [] : [...cities]
        }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchRecipients(filterDraft);
    };

    const handleSend = async (e) => {
        e.preventDefault();
        const sent = await sendNotification(message, filterDraft);
        if (sent) closeModal();
    };

    const insertLink = () => {
        const url = linkUrl.trim();
        if (!url) return;

        const textarea = messageRef.current;
        const start = textarea?.selectionStart ?? message.length;
        const end = textarea?.selectionEnd ?? message.length;
        const nextMessage = message.slice(0, start) + url + message.slice(end);

        setMessage(nextMessage);
        setLinkUrl('');
        setShowLinkInput(false);

        requestAnimationFrame(() => {
            textarea?.focus();
            const cursorPos = start + url.length;
            textarea?.setSelectionRange(cursorPos, cursorPos);
        });
    };

    const describeFilters = (filters) => {
        if (!filters || !hasAnyFilter(filters)) return ['بدون فلاتر (كل المتقدمين)'];
        const parts = [];
        if (filters.status) parts.push(`الحالة: ${STATUS_LABELS[filters.status] || filters.status}`);
        if (filters.city?.length > 0) {
            parts.push(`المدن: ${filters.city.length === cities.length ? 'الكل' : filters.city.join('، ')}`);
        }
        if (hasReferenceNumberFilter(filters)) parts.push(`الرقم المرجعي: ${filters.referenceNumber}`);
        if (filters.phoneNumber) parts.push(`الهاتف: ${filters.phoneNumber}`);
        if (filters.minAge) parts.push(`العمر من: ${filters.minAge}`);
        if (filters.maxAge) parts.push(`العمر إلى: ${filters.maxAge}`);
        return parts;
    };

    return (
        <section className="notifications-page">
            <div className="page-header">
                <button type="button" className="create-btn" onClick={openModal}>
                    <FiPlus /> إنشاء إشعار جديد
                </button>
            </div>

            {pendingRejectionCount > 0 && (
                <div className="rejection-alert">
                    <FiAlertTriangle />
                    <span>لديك <strong>{pendingRejectionCount}</strong> شخص لم يتم إعلامهم بالرفض</span>
                    <button type="button" onClick={sendRejectionBatch} disabled={isSendingRejectionBatch}>
                        {isSendingRejectionBatch ? 'جارٍ الإرسال...' : 'إرسال الآن'}
                    </button>
                </div>
            )}

            <div className="history-section">
                <h2>سجل الإشعارات</h2>

                {isLoadingHistory ? (
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                    </div>
                ) : history.length === 0 ? (
                    <div className="no-history">لا توجد إشعارات مرسلة بعد</div>
                ) : (
                    <div className="history-list">
                        {history.map((item) => (
                            <div key={item._id} className={`history-card ${item.type}`}>
                                <div className="history-card-header">
                                    <span className={`type-badge ${item.type}`}>
                                        {item.type === 'rejection' ? 'إشعار رفض تلقائي' : 'إشعار مخصص'}
                                    </span>
                                    <span className="recipient-badge">
                                        {item.recipientType === 'individual' ? 'فردي' : 'جماعي'} ({item.recipientCount})
                                    </span>
                                    <span className="history-date">
                                        {new Date(item.createdAt).toLocaleString('ar-LY')}
                                    </span>
                                </div>

                                {item.message ? (
                                    <p className="history-message">{item.message}</p>
                                ) : item.type === 'rejection' ? (
                                    <p className="history-message hint">رسائل مخصصة لكل متقدم حسب سبب رفضه</p>
                                ) : null}

                                {item.type === 'custom' && (
                                    <div className="history-filters">
                                        {describeFilters(item.filters).map((part) => (
                                            <span key={part} className="filter-chip">{part}</span>
                                        ))}
                                    </div>
                                )}

                                {item.sendProgress && !item.sendProgress.isComplete && (
                                    <div className="send-progress">
                                        <div className="send-progress__bar">
                                            <div
                                                className="send-progress__fill"
                                                style={{
                                                    width: `${item.sendProgress.total
                                                        ? Math.min(100, Math.round((item.sendProgress.sent / item.sendProgress.total) * 100))
                                                        : 0}%`
                                                }}
                                            />
                                        </div>
                                        <span>جارٍ الإرسال: {item.sendProgress.sent} / {item.sendProgress.total}</span>
                                    </div>
                                )}

                                {(!item.sendProgress || item.sendProgress.isComplete) && item.failedPhoneNumbers?.length > 0 && (
                                    <div className="failed-notice">
                                        <FiAlertTriangle />
                                        <span>لم تصل الرسالة إلى <strong>{item.failedPhoneNumbers.length}</strong> رقم</span>
                                        <button
                                            type="button"
                                            onClick={() => resendFailed(item._id)}
                                            disabled={resendingHistoryId === item._id}
                                        >
                                            <FiRefreshCw />
                                            {resendingHistoryId === item._id ? 'جارٍ إعادة الإرسال...' : 'إعادة الإرسال'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {isModalOpen && (
                <div className="modal">
                    <div className="modal-content notification-modal">
                        <h2>إنشاء إشعار جديد</h2>

                        <form className="filters-form" onSubmit={handleSearch}>
                            <div className="filters-grid">
                                <div className="filter-field">
                                    <label>رقم الهاتف</label>
                                    <input
                                        type="text"
                                        name="phoneNumber"
                                        placeholder="بحث برقم الهاتف..."
                                        value={filterDraft.phoneNumber}
                                        onChange={handleFilterChange}
                                    />
                                </div>
                                <div className="filter-field">
                                    <label>الرقم المرجعي</label>
                                    <input
                                        type="text"
                                        name="referenceNumber"
                                        placeholder="APP-0123"
                                        value={filterDraft.referenceNumber}
                                        onChange={handleFilterChange}
                                    />
                                </div>
                                <div className="filter-field">
                                    <label>الحالة</label>
                                    <select name="status" value={filterDraft.status} onChange={handleFilterChange}>
                                        {STATUS_OPTIONS.map(({ value, label }) => (
                                            <option key={value} value={value}>{label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="filter-field">
                                    <label>العمر من</label>
                                    <input
                                        type="number"
                                        name="minAge"
                                        min="0"
                                        value={filterDraft.minAge}
                                        onChange={handleFilterChange}
                                    />
                                </div>
                                <div className="filter-field">
                                    <label>العمر إلى</label>
                                    <input
                                        type="number"
                                        name="maxAge"
                                        min="0"
                                        value={filterDraft.maxAge}
                                        onChange={handleFilterChange}
                                    />
                                </div>
                            </div>

                            <div className="filter-field">
                                <div className="cities-header">
                                    <label>المدن</label>
                                    <button type="button" className="toggle-all-btn" onClick={toggleAllCities}>
                                        {filterDraft.city.length === cities.length ? 'إلغاء تحديد الكل' : 'تحديد الكل'}
                                    </button>
                                </div>
                                <div className="cities-checklist">
                                    {cities.map((city) => (
                                        <label key={city} className="city-checkbox">
                                            <input
                                                type="checkbox"
                                                checked={filterDraft.city.includes(city)}
                                                onChange={() => toggleCity(city)}
                                            />
                                            {city}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="filter-actions">
                                <button type="submit" className="apply-btn" disabled={isLoadingRecipients}>
                                    {isLoadingRecipients ? 'جارٍ البحث...' : 'بحث عن المستلمين'}
                                </button>
                            </div>
                        </form>

                        {hasSearched && (
                            <div className="recipients-panel">
                                <div className="recipients-count">
                                    <FiUsers />
                                    تم العثور على <strong>{phoneNumbers.length}</strong> رقم هاتف مطابق للفلترة
                                </div>

                                <form className="message-form" onSubmit={handleSend}>
                                    <div className="input-group">
                                        <div className="message-label-row">
                                            <label>نص الرسالة</label>
                                            <button
                                                type="button"
                                                className="insert-link-btn"
                                                onClick={() => setShowLinkInput((prev) => !prev)}
                                            >
                                                <FiLink /> إدراج رابط
                                            </button>
                                        </div>

                                        {showLinkInput && (
                                            <div className="link-insert-row">
                                                <input
                                                    type="text"
                                                    dir="ltr"
                                                    placeholder="https://..."
                                                    value={linkUrl}
                                                    onChange={(e) => setLinkUrl(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            insertLink();
                                                        }
                                                    }}
                                                />
                                                <button type="button" onClick={insertLink}>إدراج</button>
                                                <button
                                                    type="button"
                                                    onClick={() => { setShowLinkInput(false); setLinkUrl(''); }}
                                                >
                                                    إلغاء
                                                </button>
                                            </div>
                                        )}

                                        <textarea
                                            ref={messageRef}
                                            rows={4}
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            placeholder="اكتب نص الإشعار الذي سيتم إرساله للمستلمين..."
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        className="send-btn"
                                        disabled={phoneNumbers.length === 0 || !message.trim() || isSending}
                                    >
                                        <FiSend /> {isSending ? 'جارٍ الإرسال...' : 'إرسال الإشعار'}
                                    </button>
                                </form>
                            </div>
                        )}

                        <button type="button" className="close-btn" onClick={closeModal}>
                            إغلاق
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
};

export default Notifications;
